/**
 * Extracts the amenity type from a URL path for amenity landing pages
 *
 * @param {string} searchPath - The URL path (e.g., "/Miami-FL-self-storage/climate-controlled.html")
 * @returns {string|null} - The amenity value (e.g., "climate-controlled") or null if not found
 *
 * @example
 * parseAmenityFromPath('/Miami-FL-self-storage/climate-controlled.html')
 * // returns 'climate-controlled'
 *
 * parseAmenityFromPath('/Miami-FL-self-storage.html')
 * // returns null
 */
export function parseAmenityFromPath(searchPath) {
	if (!searchPath || typeof searchPath !== 'string') {
		return null;
	}

	// Match the amenity pattern: /(amenity-slug)(.html)?
	// Supports: climate-controlled, 24-hour-access, drive-up-storage-units,
	// wine-storage, storage-lockers, storage-with-electricity, storage-with-free-truck-rental
	const amenityPattern =
		/\/(climate-controlled|24-hour-access|drive-up-storage-units|wine-storage|storage-lockers|storage-with-electricity|storage-with-free-truck-rental)(\.html)?$/i;
	const match = searchPath.match(amenityPattern);

	return match ? match[1] : null;
}

/**
 * Extracts the vehicle type from a URL path for vehicle landing pages
 *
 * @param {string} searchPath - The URL path (e.g., "/Los-Angeles-CA-self-storage/car-storage.html")
 * @returns {string|null} - The vehicle value (e.g., "car") or null if not found
 *
 * @example
 * parseVehicleFromPath('/Los-Angeles-CA-self-storage/car-storage.html')
 * // returns 'car'
 *
 * parseVehicleFromPath('/Los-Angeles-CA-self-storage.html')
 * // returns null
 */
export function parseVehicleFromPath(searchPath) {
	if (!searchPath || typeof searchPath !== 'string') {
		return null;
	}

	// Match the vehicle pattern: /(car|boat|rv)-storage(.html)?
	const vehiclePattern = /\/(car|boat|rv)-storage(\.html)?$/i;
	const match = searchPath.match(vehiclePattern);

	return match ? match[1].toLowerCase() : null;
}

/**
 * Mapping of amenity URL slugs to their query parameter equivalents
 * Used by API Gateway to transform amenity params into filter params
 */
export const AMENITY_TO_FILTER_MAP = {
	'climate-controlled': 'climateControlled',
	'24-hour-access': 'twentyFourHourAccess',
	'drive-up-storage-units': 'driveUp',
	'wine-storage': 'onlyWine',
	'storage-lockers': 'onlyLocker',
	'storage-with-electricity': 'power',
	'storage-with-free-truck-rental': 'freeTruckRental',
};

/**
 * Gets the filter parameter name for a given amenity slug
 *
 * @param {string} amenitySlug - The amenity slug from URL (e.g., "climate-controlled")
 * @returns {string|null} - The filter param name (e.g., "climateControlled") or null
 */
export function getFilterParamForAmenity(amenitySlug) {
	return AMENITY_TO_FILTER_MAP[amenitySlug] || null;
}

/**
 * Mapping of size URL slugs to their sqft parameter equivalents
 * These ranges match the API Gateway's unitSizeUtil.getSqftRange() logic
 */
export const SIZE_SLUG_TO_SQFT_MAP = {
	'mini-storage': '0-25',
	'5x5-storage-units': '20-37',
	'5x10-storage-units': '37-62',
	'5x15-storage-units': '62-87',
	'10x10-storage-units': '87-125',
	'10x15-storage-units': '125-175',
	'10x20-storage-units': '175-250',
	'10x25-storage-units': '250-300',
	'10x30-storage-units': '300-9999',
};

/**
 * Extracts the size type from a URL path for size landing pages
 *
 * @param {string} searchPath - The URL path (e.g., "/San-Antonio-TX-self-storage/5x5-storage-units.html")
 * @returns {string|null} - The size slug (e.g., "5x5-storage-units") or null if not found
 *
 * @example
 * parseSizeFromPath('/San-Antonio-TX-self-storage/5x5-storage-units.html')
 * // returns '5x5-storage-units'
 *
 * parseSizeFromPath('/San-Antonio-TX-self-storage.html')
 * // returns null
 */
export function parseSizeFromPath(searchPath) {
	if (!searchPath || typeof searchPath !== 'string') {
		return null;
	}

	// Match the size pattern: /(size)-storage-units or /mini-storage(.html)?
	// Supports: mini-storage, 5x5, 5x10, 5x15, 10x10, 10x15, 10x20, 10x25, 10x30
	const sizePattern =
		/\/(mini-storage|(5x5|5x10|5x15|10x10|10x15|10x20|10x25|10x30)-storage-units)(\.html)?$/i;
	const match = searchPath.match(sizePattern);

	if (match) {
		// Return the full slug (e.g., "5x5-storage-units" or "mini-storage")
		return match[1];
	}

	return null;
}

/**
 * Gets the sqft parameter value for a given size slug
 *
 * @param {string} sizeSlug - The size slug from URL (e.g., "5x5-storage-units")
 * @returns {string|null} - The sqft value (e.g., "0-37") or null
 */
export function getSqftForSize(sizeSlug) {
	return SIZE_SLUG_TO_SQFT_MAP[sizeSlug] || null;
}

/**
 * Mapping of size URL slugs to their filter bucket sqft values.
 * These match the newStorageSizeBuckets values used by SearchFilters radio buttons.
 */
export const SIZE_SLUG_TO_FILTER_SQFT_MAP = {
	'mini-storage': '0-25',
	'5x5-storage-units': '0-25',
	'5x10-storage-units': '26-75',
	'5x15-storage-units': '26-75',
	'10x10-storage-units': '76-200',
	'10x15-storage-units': '76-200',
	'10x20-storage-units': '76-200',
	'10x25-storage-units': '201-999',
	'10x30-storage-units': '201-999',
};

/**
 * Gets the filter bucket sqft value for a given size slug.
 * Used to pre-select the correct radio button in SearchFilters on SLP pages.
 *
 * @param {string} sizeSlug - The size slug from URL (e.g., "5x5-storage-units")
 * @returns {string|null} - The filter bucket value (e.g., "0-25") or null
 */
export function getFilterSqftForSize(sizeSlug) {
	return SIZE_SLUG_TO_FILTER_SQFT_MAP[sizeSlug] || null;
}

export const NEAR_ME_KEYWORD_HEADINGS = {
	'rv-storage': 'RV Storage',
	'car-storage': 'Car Storage',
	'boat-storage': 'Boat Storage',
	'climate-controlled-storage': 'Climate Controlled Storage',
	'drive-up-storage': 'Drive Up Storage',
	'24-hour-storage': '24 Hour Storage',
	'mini-storage': 'Mini Storage',
	'locker-storage': 'Storage Lockers',
	'storage-with-electricity': 'Storage With Electricity',
	'wine-storage': 'Wine Storage',
	'storage-with-free-truck-rental': 'Storage With Free Truck Rental',
	'vehicle-storage': 'Vehicle Storage',
	'portable-storage': 'Portable Storage',
	'motorcycle-storage': 'Motorcycle Storage',
	'business-storage': 'Business Storage',
	'atv-storage': 'ATV Storage',
	'bicycle-storage': 'Bicycle Storage',
	'dirt-bike-storage': 'Dirt Bike Storage',
	'golf-cart-storage': 'Golf Cart Storage',
	'college-storage': 'College Storage',
	'truck-storage': 'Truck Storage',
	'jet-ski-storage': 'Jet Ski Storage',
	'kayak-storage': 'Kayak Storage',
	'military-storage': 'Military Storage',
	'storage-deals': 'Storage Deals',
	'5x5-storage': '5x5 Storage Units',
	'5x10-storage': '5x10 Storage Units',
	'5x15-storage': '5x15 Storage Units',
	'10x10-storage': '10x10 Storage Units',
	'10x15-storage': '10x15 Storage Units',
	'10x20-storage': '10x20 Storage Units',
	'10x25-storage': '10x25 Storage Units',
	'10x30-storage': '10x30 Storage Units',
};

/**
 * Returns "[Label] Near You" for near-me pages based on URL keyword.
 * Returns null for non-near-me pages or unrecognised keywords.
 *
 * @param {object} match - React Router match object
 * @returns {string|null}
 */
export function getNearMeHeadline(match) {
	const url = match?.url || '';
	if (!url.includes('/near-me/')) return null;

	const keyword = match?.params?.keyword;
	return keyword && NEAR_ME_KEYWORD_HEADINGS[keyword]
		? `${NEAR_ME_KEYWORD_HEADINGS[keyword]} Near You`
		: null;
}

